import Checkbox from "components/checkbox/Checkbox";
import messages from "messages";
import getClasses from "utils/getClasses";
import { useCallback, useState } from "react";
import { Form, Row, Col, Button, InputGroup, Spinner } from "react-bootstrap";
import ErrorMessage from "components/error-message";
import { useAuthModals } from "contexts/auth-modals-context";
import registerNewPatient from "services/auth/registerNewPatient";
import { PASSWORD_PATTERN } from "constants/passwordPattern";
import { AmplitudePageName, track } from "utils/amplitude";
// styles
import styles from "./styles.module.scss";

interface Props {
  amplitudePageName: AmplitudePageName;
}

/**
 * renders form UI for ready to match section in client facing landing page
 */

const ReadyToMatchForm: React.FC<Props> = ({ amplitudePageName }) => {
  const [value, setValue] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }>({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
  });
  const { setCustomEmailSentModalProps, openEmailSentModal } = useAuthModals();

  const togglePassword = (event) => {
    event.preventDefault();
    setRevealPassword(!revealPassword);
  };

  const [error, setError] = useState<any>();
  const [validated, setValidated] = useState(false);
  const [unmetPassword, setUnmetPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [revealPassword, setRevealPassword] = useState(false);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      const form = event.currentTarget;
      if (!form.checkValidity()) {
        setError("Please fill out all of the required fields correctly");
        setValidated(true);
        setUnmetPassword(true);
        return;
      }
      setBusy(true);
      try {
        await registerNewPatient(
          {
            email: value.email,
            password: value.password,
            invite_code: null,
          },
          {
            first_name: value.firstName,
            last_name: value.lastName,
          },
        );
        setCustomEmailSentModalProps({
          email: value.email,
        });
        openEmailSentModal();
      } catch (error) {
        setError(error);
      } finally {
        setBusy(false);
      }
    },
    [setCustomEmailSentModalProps, openEmailSentModal, value],
  );

  const handleChange = useCallback((e) => {
    setValue((s) => ({ ...s, [e.target.name]: e.target.value }));
  }, []);

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit}>
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error.toString()}
        />
      )}
      <Form.Row>
        <Form.Group as={Col} md="6" xs="6" controlId={"firstName"}>
          <Form.Label className={styles["ready-to-match-form__form-label"]}>
            {messages.firstName} <span className="required">*</span>
          </Form.Label>
          <Form.Control
            required
            type="text"
            placeholder={messages.enterYourFirstName}
            name="firstName"
            value={value.firstName}
            onChange={handleChange}
            onClick={() =>
              track(amplitudePageName, "first-name-field", "click")
            }
          />
        </Form.Group>
        <Form.Group as={Col} md="6" xs="6" controlId={"lastName"}>
          <Form.Label className={styles["ready-to-match-form__form-label"]}>
            {messages.lastName} <span className="required">*</span>
          </Form.Label>
          <Form.Control
            required
            type="text"
            placeholder={messages.enterYourLastName}
            name="lastName"
            value={value.lastName}
            onChange={handleChange}
            onClick={() => track(amplitudePageName, "last-name-field", "click")}
          />
        </Form.Group>
      </Form.Row>
      <Form.Group controlId={"email"}>
        <Form.Label className={styles["ready-to-match-form__form-label"]}>
          Email Address <span className="required">*</span>
        </Form.Label>

        <Form.Control
          required
          type="email"
          name="email"
          placeholder="Enter Email Address"
          value={value.email}
          onChange={handleChange}
          onClick={() => track(amplitudePageName, "email-field", "click")}
        />
      </Form.Group>

      <Form.Group controlId="password">
        <Form.Label className={styles["ready-to-match-form__form-label"]}>
          Password
        </Form.Label>
        <InputGroup
          className={getClasses("align-items-center mb-2", styles["password"])}
        >
          <Form.Control
            required
            name="password"
            type={revealPassword ? "text" : "password"}
            placeholder="Enter password"
            pattern={PASSWORD_PATTERN.source}
            value={value.password}
            onChange={handleChange}
            onClick={() => track(amplitudePageName, "password-field", "click")}
            autoComplete="current-password"
          />
          <InputGroup.Append onClick={togglePassword}>
            <InputGroup.Text>
              {revealPassword ? "Hide" : "Show"}
            </InputGroup.Text>
          </InputGroup.Append>
        </InputGroup>
      </Form.Group>

      <div className={`light-gray fs-12 ${value.password ? "" : "d-none"}`}>
        <div className={`px-0 ${unmetPassword ? "red" : ""}`}>
          <Row>
            <Col md={5} as="ul">
              <li
                className={
                  value.password.toLowerCase() !== value.password
                    ? "text-success"
                    : ""
                }
              >
                1 uppercase letter
              </li>
              <li
                className={
                  value.password.toUpperCase() !== value.password
                    ? "text-success"
                    : ""
                }
              >
                1 lowercase letter
              </li>
              <li
                className={
                  /[\^=+$\-*.[\]{}()?"!@#%&\\,><':;|_~`]/.test(value.password)
                    ? "text-success"
                    : ""
                }
              >
                1 special character
              </li>
            </Col>
            <Col md={5} as="ul">
              <li className={/\d/.test(value.password) ? "text-success" : ""}>
                1 number
              </li>
              <li className={value.password.length >= 10 ? "text-success" : ""}>
                10 characters or more
              </li>
            </Col>
          </Row>
        </div>
      </div>

      {/* <Form.Group controlId={"password"}>
        <Form.Label className={styles["ready-to-match-form__form-label"]}>
          {messages.password} <span className="required">*</span>
        </Form.Label>
        <Form.Control
          required
          type="password"
          placeholder={messages.createAPassword}
          name="password"
          value={value.password}
          onChange={handleChange}
        />
      </Form.Group>
      <Form.Group controlId={"confirmPassword"}>
        <Form.Label className={styles["ready-to-match-form__form-label"]}>
          {messages.confirmPassword}
        </Form.Label>
        <Form.Control
          required
          type="password"
          placeholder={messages.createAPassword}
          name="confirmPassword"
          value={value.confirmPassword}
          onChange={handleChange}
        />
      </Form.Group> */}
      <Form.Group
        className={styles["ready-to-match-form__form-group--form-check"]}
      >
        <Checkbox
          variant={"rectangle"}
          required
          name="terms"
          label={
            <div className={styles["ready-to-match-form__form-check"]}>
              <span>{messages.iAmOver181}</span>

              <Button
                variant={"link"}
                className={styles["ready-to-match-form__link"]}
                onClick={() =>
                  track(amplitudePageName, "terms-of-user-link", "click")
                }
              >
                {messages.termsOfUse}
              </Button>

              <span>{messages.iAmOver183}</span>

              <Button
                variant={"link"}
                className={styles["ready-to-match-form__link"]}
                onClick={() =>
                  track(amplitudePageName, "privacy-policy-link", "click")
                }
              >
                {messages.iAmOver184}
              </Button>
            </div>
          }
          onChange={(e) => {
            track(amplitudePageName, "agree-to-terms-checkbox", "click");
            handleChange(e);
          }}
        />
      </Form.Group>
      <Button
        type="submit"
        variant={"primary"}
        className={styles["ready-to-match-form__action"]}
        disabled={busy}
        onClick={() =>
          track(amplitudePageName, "get-your-free-account-button", "click")
        }
      >
        {messages.getYourFreeAccount}{" "}
        {busy && (
          <Spinner
            as="span"
            animation="grow"
            size="sm"
            role="status"
            aria-hidden="true"
          />
        )}
      </Button>
    </Form>
  );
};

export default ReadyToMatchForm;
